import React from 'react';
import PropTypes from 'prop-types';
import AssociatedContactSelector from '../../../components/tasks/associated_contact_selector';
import PersonContactForm from '../person_contact_form';
import CompanyContactForm from '../company_contact_form';
import LeadFormFields from '../lead_form_fields';
import { Button } from 'components/ui/button';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';
import { addNumber, changePrimary, deleteNumber, phoneNumberChange } from '../helper/person_phone_number';
import { RADIO_OPTIONS } from './helper';
import { Form } from 'react-bootstrap-5';
import LoadingSpinner from 'components/ui/loading_spinner';

export default function FormDisplay({
  allLeadReferralSources,
  company,
  contactIsPerson,
  crmContactsEnabled,
  databaseContact,
  docAccessLevels,
  fetchingContact,
  handleSave,
  hideLeadModal,
  lead,
  leadData,
  leaseChecked,
  leadStatuses,
  listings,
  person,
  persistAddress,
  project,
  radioSelected,
  requestSending,
  saleChecked,
  setCompany,
  setDatabaseContact,
  setLeadData,
  setPerson,
  setPersistAddress,
  useVaultGates,
  valid
}) {
  const areFieldsRequired = !lead;

  function addressCompanyFieldChange(newValue, field) {
    setCompany({ ...company, address: { ...company?.address, [field]: newValue } });
  }

  function basicCompanyFieldChange(newValue, field) {
    setCompany({ ...company, [field]: newValue });
  }

  function basicPersonFieldChange(newValue, field) {
    setPerson({ ...person, [field]: newValue });
  }

  function basicLeadFieldChange(newValue, field) {
    setLeadData(prevData => ({ ...prevData, [field]: newValue }));
  }

  function phoneNumberPersonFieldChange(id, newValue, field) {
    setPerson({ ...person, phoneNumbers: phoneNumberChange(id, newValue, field, person.phoneNumbers) });
  }

  function addPhoneNumber() {
    setPerson({ ...person, phoneNumbers: addNumber(person.phoneNumbers) });
  }

  function changePhoneNumberPrimary(id, makePrimary) {
    setPerson({ ...person, phoneNumbers: changePrimary(id, makePrimary, person.phoneNumbers) });
  }

  function deletePhoneNumber(id) {
    setPerson({ ...person, phoneNumbers: deleteNumber(id, person.phoneNumbers) });
  }

  function modelAdded(contact) {
    setDatabaseContact(contact);
  }

  let contactForm = '';
  switch (radioSelected) {
  case RADIO_OPTIONS.NEW_PERSON:
    contactForm = 'person';
    break;
  case RADIO_OPTIONS.NEW_COMPANY:
    contactForm = 'company';
    break;
  case RADIO_OPTIONS.SEARCH_CONTACTS:
    contactForm = 'search';
    break;
  }
  if (lead) contactForm = contactIsPerson ? 'person' : 'company';

  return (
    <form className="form-vertical-field-container" onSubmit={handleSave}>
      {fetchingContact ? (
        <div className="d-flex justify-content-center">
          <LoadingSpinner />
        </div>
      ) : (
        <React.Fragment>
          {contactForm === 'search' && (
            <Form.Group className="form-group" name="select-contact-form">
              <Form.Label>Contact</Form.Label>
              <AssociatedContactSelector
                className="mb-4"
                crmSearch={crmContactsEnabled}
                selectedValue={databaseContact}
                onModelAdded={modelAdded}
              />
            </Form.Group>
          )}
          {contactForm === 'person' && (
            <PersonContactForm
              accessibleDatabaseContacts={lead?.accessibleDatabaseContacts}
              areFieldsRequired={areFieldsRequired}
              displayName={lead?.displayName}
              person={person}
              onAddPhoneNumber={addPhoneNumber}
              onChangePhoneNumberPrimary={changePhoneNumberPrimary}
              onDeletePhoneNumber={deletePhoneNumber}
              onFieldChange={basicPersonFieldChange}
              onPhoneNumberChange={phoneNumberPersonFieldChange}
            />
          )}
          {contactForm === 'company' && (
            <CompanyContactForm
              accessibleDatabaseContacts={lead?.accessibleDatabaseContacts}
              areFieldsRequired={areFieldsRequired}
              company={company}
              displayName={lead?.displayName}
              persistAddress={persistAddress}
              onAddressChange={addressCompanyFieldChange}
              onFieldChange={basicCompanyFieldChange}
              onToggleAddressFields={() => setPersistAddress(!persistAddress)}
            />
          )}
        </React.Fragment>
      )}
      <LeadFormFields
        docAccessLevels={docAccessLevels}
        lead={lead}
        leadData={leadData}
        leadReferralSources={allLeadReferralSources}
        leadStatuses={leadStatuses}
        leaseChecked={leaseChecked}
        listings={listings}
        project={project}
        saleChecked={saleChecked}
        useVaultGates={useVaultGates}
        onFieldChange={basicLeadFieldChange}
      />

      <Modal.Footer>
        <Button label="Cancel" name="cancel" type="button" variant="ghost" onClick={() => hideLeadModal()} />
        <Button disabled={!valid || requestSending} label="Save" name="save" type="submit" />
      </Modal.Footer>
    </form>
  );
}

FormDisplay.propTypes = {
  allLeadReferralSources: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired
  })).isRequired,
  company: PropTypes.object,
  contactIsPerson: PropTypes.bool.isRequired,
  crmContactsEnabled: PropTypes.bool,
  databaseContact: PropTypes.object,
  docAccessLevels: PropTypes.array,
  fetchingContact: PropTypes.bool.isRequired,
  handleSave: PropTypes.func.isRequired,
  hideLeadModal: PropTypes.func.isRequired,
  lead: PropTypes.object,
  leadData: PropTypes.object,
  leadStatuses: PropTypes.array,
  leaseChecked: PropTypes.bool.isRequired,
  listings: PropTypes.array,
  persistAddress: PropTypes.bool.isRequired,
  person: PropTypes.object,
  project: PropTypes.object,
  radioSelected: PropTypes.string.isRequired,
  requestSending: PropTypes.bool,
  saleChecked: PropTypes.bool.isRequired,
  setCompany: PropTypes.func.isRequired,
  setDatabaseContact: PropTypes.func.isRequired,
  setLeadData: PropTypes.func.isRequired,
  setPersistAddress: PropTypes.func.isRequired,
  setPerson: PropTypes.func.isRequired,
  useVaultGates: PropTypes.bool.isRequired,
  valid: PropTypes.bool.isRequired
};
